import React, { useState } from 'react';

import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { sendAnalyticEvent } from '../../../data/actions/analytics';
import { postContactForm } from './post-contact-form';
import { Toast } from './toast';

export const SubmissionForm = () => {
  const content = {
    formTitle: 'Contact us for personalized support',
    formName: 'Your Name',
    formEmail: 'Your Email*',
    formMessage: 'Your Message*',
    formSubmit: 'Send Message',
    successMessage: 'Message sent successfully!',
    errorRequired: 'This field is required',
    errorEmail: 'Please enter a valid email address',
    errorDefault: 'An error occurred. Please try again later.',
    sending_message: 'Sending...'
  };

  const {
    formTitle,
    formName,
    formEmail,
    formMessage,
    formSubmit,
    successMessage,
    errorRequired,
    errorEmail,
    errorDefault,
    sending_message
  } = content;

  // Removed displayEmail prop
  const displayEmail = '';

  const user =
    typeof window !== 'undefined' && localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user') || ' ')
      : null;

  const userEmail = user?.email || '';

  const [formData, setFormData] = useState({
    name: '',
    email: userEmail,
    message: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    message: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  const validateEmail = (email: string) => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));

    // Clear errors when user starts typing
    setErrors((prev) => ({
      ...prev,
      [id]: ''
    }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true
    }));

    // Validate on blur
    validateField(field, formData[field as keyof typeof formData]);
  };

  const validateField = (field: string, value: string) => {
    let error = '';

    if (!value && (field === 'email' || field === 'message')) {
      error = errorRequired;
    } else if (field === 'email' && !validateEmail(value)) {
      error = errorEmail;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error
    }));

    return !error;
  };

  const submitForm = async () => {
    const { name = null, email, message } = formData;
    const data = {
      fullName: name,
      email,
      message,
      userId: user?.id
    };

    setIsSubmitting(true);

    try {
      // await new Promise((resolve) => setTimeout(resolve, 1000))
      const response = await postContactForm(data);

      if (response.status === 200) {
        toast.success(successMessage);

        dispatch(
          sendAnalyticEvent({
            event: 'form_submission_status',
            data: {
              status: 'success'
            }
          })
        );
      }
    } catch (error: any) {
      toast.error(`${errorDefault} ${displayEmail}`);

      const errorStatus = error?.response?.status || 'Unknown error';
      dispatch(
        sendAnalyticEvent({
          event: 'form_submission_status',
          data: {
            status: errorStatus
          }
        })
      );
    } finally {
      setIsSubmitting(false);

      setFormData({
        name: '',
        email: userEmail,
        message: ''
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // // Mark only required fields as touched
    setTouched({
      name: true,
      email: true,
      message: true
    });

    // Validate only required fields
    const emailValid = validateField('email', formData.email);
    const messageValid = validateField('message', formData.message);

    if (emailValid && messageValid) {
      await submitForm();
    }
  };

  return (
    <div
      className='w-full rounded-2xl bg-[#f6f6f6] p-6 shadow-md lg:p-8'
      data-testid='submission-form'
    >
      <h2
        className='mb-6 w-fit text-left text-[20px] font-bold leading-[130%] text-[#1d1d1d] lg:text-[25px]'
        data-testid='form-title'
      >
        {formTitle}
      </h2>
      <form
        onSubmit={handleSubmit}
        className='flex flex-col space-y-4 lg:space-y-6'
        noValidate
        data-testid='contact-form'
      >
        <div
          className='mb-4 flex flex-col gap-6 lg:flex-row'
          data-testid='form-inputs-container'
        >
          <div
            className='w-full lg:w-1/3'
            data-testid='name-input-container'
          >
            <label
              className='mb-2 flex items-center font-normal text-[#1d1d1d]'
              data-testid='name-label'
            >
              {formName}
            </label>
            <input
              type='text'
              id='name'
              value={formData.name}
              onChange={handleInputChange}
              onBlur={() => handleBlur('name')}
              className={`w-full rounded-md border ${
                touched.name && errors.name ? 'border-red-500' : 'border-[#c7c7c7]'
              } bg-white px-4 py-3 transition-all hover:border-[#5F30E2] focus:outline-none focus:ring-2 focus:ring-[#5F30E2]`}
              placeholder={formName}
              data-testid='name-input'
            />
          </div>
          <div
            className='w-full lg:w-2/3'
            data-testid='email-input-container'
          >
            <label
              className='mb-2 flex items-center justify-between gap-4 font-normal text-[#1d1d1d]'
              data-testid='email-label'
            >
              <span>
                {formEmail}
                <span className='text-red-500'>*</span>
              </span>

              {touched.email && errors.email && (
                <span
                  className='text-sm text-red-500 transition-opacity duration-200 ease-in-out'
                  data-testid='email-error'
                >
                  {errors.email}
                </span>
              )}
            </label>

            <input
              type='email'
              id='email'
              value={formData.email}
              onChange={handleInputChange}
              onBlur={() => handleBlur('email')}
              className={`w-full rounded-md border ${
                touched.email && errors.email ? 'border-red-500' : 'border-[#c7c7c7]'
              } bg-white px-4 py-3 transition-all ${
                !(touched.email && errors.email) &&
                'hover:border-[#5F30E2] focus:outline-none focus:ring-2 focus:ring-[#5F30E2]'
              }`}
              placeholder={formEmail}
              data-testid='email-input'
            />
          </div>
        </div>

        <div
          style={{ marginTop: '12px' }}
          data-testid='message-input-container'
        >
          <label
            className='mb-2 flex items-center justify-between gap-4 font-normal text-[#1d1d1d]'
            data-testid='message-label'
          >
            <span>
              {formMessage}
              <span className='text-red-500'>*</span>
            </span>
            {touched.message && errors.message && (
              <span
                className='text-sm text-red-500'
                data-testid='message-error'
              >
                {errors.message}
              </span>
            )}
          </label>
          <textarea
            id='message'
            value={formData.message}
            onChange={handleInputChange}
            onBlur={() => handleBlur('message')}
            rows={5}
            className={`w-full rounded-md border ${
              touched.message && errors.message ? 'border-red-500' : 'border-[#c7c7c7]'
            } max-h-[450px] min-h-[146px] bg-white px-4 py-3 transition-all ${
              !(touched.message && errors.message) &&
              'hover:border-[#5F30E2] focus:outline-none focus:ring-2 focus:ring-[#5F30E2]'
            }`}
            placeholder={formMessage}
            required
            data-testid='message-input'
          />
        </div>
        <button
          style={{ marginTop: '18px' }}
          type='submit'
          disabled={isSubmitting || !formData.email || !formData.message || !validateEmail(formData.email)}
          className={`mx-auto flex w-full cursor-pointer items-center justify-center rounded-lg py-4 text-lg font-bold text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#5F30E2] lg:mx-0 lg:w-[235px] ${
            !formData.email || !formData.message || !validateEmail(formData.email)
              ? '!cursor-not-allowed bg-gray-400'
              : 'bg-[#5F30E2] transition-colors hover:bg-[#4B26B3]'
          }`}
          onClick={() =>
            dispatch(
              sendAnalyticEvent({
                event: 'form_submit',
                data: {
                  button: 'submit'
                }
              })
            )
          }
          data-testid='submit-button'
        >
          {isSubmitting ? (
            <>
              <svg
                className='mr-2 h-5 w-5 animate-spin text-white'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                data-testid='loading-spinner'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
              <span data-testid='sending-message'>{sending_message}</span>
            </>
          ) : (
            <span data-testid='submit-text'>{formSubmit}</span>
          )}
        </button>
      </form>

      <Toast />
    </div>
  );
};
